// Dependency picker modal — FE-043b parity v2.
//
// Opens from the "Add dependency" affordance on the Commitment
// detail page. Lets the user search the entity's Commitments and
// pick one to add as a finish-to-start dependency. The actual
// add mutation isn't wired (BE has the model but no REST endpoint
// yet); the modal hands the picked id back to the caller via
// ``onPick`` and the caller surfaces a "coming soon" toast for
// now. Search is debounced and powered by the existing list
// query, so dependency search inherits the FE-043b search-field
// expansion automatically.

import { Search, X } from "lucide-react";
import { useEffect, useState } from "react";

import { useCommitmentListQuery } from "./hooks";


export interface DependencyPickerModalProps {
  open: boolean;
  /** Commitment id we're picking a dependency FOR — excluded from
   * the result list so a Commitment can't depend on itself. */
  selfCommitmentId: string;
  onCancel: () => void;
  onPick: (commitmentId: string, title: string) => void;
}


export function DependencyPickerModal({
  open, selfCommitmentId, onCancel, onPick,
}: DependencyPickerModalProps) {
  const [needle, setNeedle] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    if (!open) {
      setNeedle("");
      setDebounced("");
      return;
    }
    const t = window.setTimeout(() => setDebounced(needle.trim()), 200);
    return () => window.clearTimeout(t);
  }, [needle, open]);

  const query = useCommitmentListQuery({
    filters: debounced ? { search: debounced } : {},
    sortBy: "updated_at",
    sortDir: "desc",
    limit: 20,
    offset: 0,
  });

  if (!open) return null;
  const rows = (query.data?.rows ?? []).filter(
    (r) => r.commitment_id !== selfCommitmentId,
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="dependency-picker-title"
      data-testid="dependency-picker-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-text-primary/40"
    >
      <div className="flex w-full max-w-md flex-col gap-3 rounded-lg border border-border-subtle bg-raised p-4 shadow-lg">
        <header className="flex items-center justify-between">
          <h2
            id="dependency-picker-title"
            className="text-sm font-semibold text-text-primary"
          >
            Add dependency
          </h2>
          <button
            type="button"
            data-testid="dependency-picker-close"
            onClick={onCancel}
            aria-label="Close"
            className="rounded p-1 text-text-tertiary hover:bg-recessed hover:text-text-primary"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </header>

        <label className="relative flex items-center">
          <Search
            size={14}
            aria-hidden="true"
            className="pointer-events-none absolute left-2.5 text-text-tertiary"
          />
          <input
            type="search"
            data-testid="dependency-picker-search"
            autoFocus
            value={needle}
            onChange={(e) => setNeedle(e.target.value)}
            placeholder="Search Commitments by title, number, account…"
            className="w-full rounded-md border border-border-subtle bg-canvas/40 py-2 pl-7 pr-3 text-sm text-text-primary outline-none focus:border-teal"
          />
        </label>

        <div
          data-testid="dependency-picker-results"
          className="max-h-72 overflow-y-auto rounded-md border border-border-subtle"
        >
          {query.isLoading ? (
            <p className="px-3 py-6 text-center text-xs text-text-tertiary">
              Searching…
            </p>
          ) : rows.length === 0 ? (
            <p
              data-testid="dependency-picker-empty"
              className="px-3 py-6 text-center text-xs text-text-tertiary"
            >
              {debounced ? "No matches." : "Start typing to search."}
            </p>
          ) : (
            <ul role="list" className="flex flex-col">
              {rows.map((row) => (
                <li key={row.commitment_id}>
                  <button
                    type="button"
                    data-testid={`dependency-picker-pick-${row.commitment_id}`}
                    onClick={() => onPick(row.commitment_id, row.title)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-recessed"
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-text-primary">
                        {row.title}
                      </span>
                      <span className="text-[11px] text-text-tertiary">
                        {row.commitment_number}{row.account_name ? ` · ${row.account_name}` : ""}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
